import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import ScenarioDisplay from "../components/ScenarioDisplay";
import PH4456Form from "../components/PH4456Form";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Textarea,
  VStack,
  HStack,
  Grid,
  GridItem,
  Text,
  useToast,
  Spinner,
  Divider,
  Card,
  CardBody,
  useColorModeValue,
  Checkbox,
  FormHelperText,
  Switch,
  Badge,
} from "@chakra-ui/react";
import { ViewIcon, Icon, ArrowBackIcon, CheckIcon } from "@chakra-ui/icons";
import { FiShare2 } from "react-icons/fi";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ShareScenarioModal from "../components/ShareScenarioModal";

const TRAINING_LEVELS = ["Paramedic", "EMT", "AEMT", "CCPC", "FPC"];
const BODY_SYSTEMS = [
  "AI-Defined",
  "Cardiovascular",
  "Respiratory",
  "Neurological",
  "Gastrointestinal",
  "Musculoskeletal",
  "Integumentary",
  "Endocrine",
  "Genitourinary",
  "Reproductive",
  "Immunologic",
  "Psychiatric",
  "Trauma",
];
const PATIENT_AGES = [
  "AI-Defined",
  "Neonate (0-30 days)",
  "Infant (1-12 months)", 
  "Toddler (1-2 years)",
  "Preschool (3-5 years)",
  "School Age (6-12 years)",
  "Adolescent (13-18 years)",
  "Adult (19-64 years)",
  "Geriatric (65+ years)",
  "Specify"
];
const BIOLOGICAL_SEX = ["AI-Defined", "Male", "Female", "Intersex"];

const DIFFICULTY_DESCRIPTIONS = {
  1: "Stable patient; minimal or no complaints; no intervention.",
  2: "Stable patient with simple treatable pathology; condition unlikely to worsen. Student has reasonable interventions available but the patient will remain relatively stable if untreated.",
  3: "Relatively stable but with treatable pathology requiring intervention. Failure to appropriately intervene will result in deterioration but NOT cardiac arrest.",
  4: "Severely ill/injured; signs of shock and diminished mental status. Student MUST perform critical interventions quickly or the patient will enter cardiac arrest.",
  5: "Cardiac arrest in progress or peri-arrest condition where cardiac arrest is imminent and unavoidable even with proper treatment."
};

const DIFFICULTY_COLORS = {
  1: "blue.200",
  2: "blue.300",
  3: "blue.400",
  4: "blue.500",
  5: "blue.600",
};

// Session storage keys for generation recovery
const STORAGE_KEYS = {
  ACTIVE_GENERATION: 'scenario_active_generation',
  FORM_DATA: 'scenario_form_data',
  SESSION_ID: 'scenario_session_id'
};

// Helper to save generation state
const saveGenerationState = (sessionId, formData) => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.ACTIVE_GENERATION, 'true');
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
    sessionStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formData));
  } catch (err) {
    console.warn('Failed to save generation state:', err);
  }
};

// Helper to clear generation state
const clearGenerationState = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_GENERATION);
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    sessionStorage.removeItem(STORAGE_KEYS.FORM_DATA);
  } catch (err) {
    console.warn('Failed to clear generation state:', err);
  }
};

// Helper to check for active generation
const checkForActiveGeneration = () => {
  try {
    const isActive = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_GENERATION) === 'true';
    const sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
    const formDataStr = sessionStorage.getItem(STORAGE_KEYS.FORM_DATA);
    const formData = formDataStr ? JSON.parse(formDataStr) : null;
    
    return { isActive, sessionId, formData };
  } catch (err) {
    console.warn('Failed to check for active generation:', err);
    return { isActive: false, sessionId: null, formData: null };
  }
};

// Component to show real-time progress during scenario generation
/*
const StreamingProgressDisplay = ({ currentStatus, completedElements, recentEvents }) => {
  const elementsList = [
    { key: 'routing', label: 'Clinical Analysis' },
    { key: 'patientInfo', label: 'Patient Demographics' },
    { key: 'sceneSetup', label: 'Scene Setup' },
    { key: 'chiefComplaint', label: 'Chief Complaint' },
    { key: 'history', label: 'Medical History' },
    { key: 'primaryAssessment', label: 'Primary Assessment' },
    { key: 'vitalSigns', label: 'Vital Signs' },
    { key: 'interventions', label: 'Interventions' },
    { key: 'learningObjectives', label: 'Learning Objectives' }
  ];

  return (
    <VStack align="stretch" spacing={4}>
      <Text 
        color="blue.200" 
        fontSize="md" 
        textAlign="center"
        minH="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
      >
        {currentStatus || 'Initializing...'}
      </Text>
      
      <VStack align="stretch" spacing={2} mt={2}>
        {elementsList.map(({ key, label }) => (
          <HStack key={key} spacing={3}>
            {completedElements[key] ? (
              <CheckIcon color="green.400" boxSize={4} />
            ) : (
              <Box 
                w="16px" 
                h="16px" 
                borderRadius="full" 
                border="2px" 
                borderColor="gray.600" 
              />
            )}
            <Text 
              color={completedElements[key] ? "green.300" : "gray.500"} 
              fontSize="sm"
              fontWeight={completedElements[key] ? "semibold" : "normal"}
            >
              {label}
            </Text>
          </HStack>
        ))}
      </VStack>
      
      {recentEvents && Array.isArray(recentEvents) && recentEvents.length > 0 && (
        <Box mt={4} pt={4} borderTop="1px" borderColor="gray.700">
          <Text fontSize="xs" color="gray.500" mb={2}>Recent Updates:</Text>
          <VStack align="stretch" spacing={1}>
            {recentEvents.slice(-3).map((event, idx) => (
              <Text key={idx} fontSize="xs" color="gray.400">
                • {event?.message || 'Processing...'}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};
*/

// Simple fallback loader - always works even if streaming fails
const SimpleFallbackLoader = () => {
  const [dots, setDots] = useState('');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <VStack spacing={4}>
      <Text 
        color="blue.300" 
        fontSize="lg" 
        textAlign="center"
        fontWeight="medium"
      >
        Generating your clinical scenario{dots}
      </Text>
      <VStack spacing={2}>
        <Text color="gray.400" fontSize="sm" textAlign="center">
          This typically takes several minutes depending on complexity
        </Text>
        <Text color="yellow.300" fontSize="xs" textAlign="center" maxW="400px">
          If generation fails, check your scenario library in 10 minutes. The scenario may still be processing in the background. If it's not there, contact support@elevationems.com
        </Text>
      </VStack>
      <Box mt={2}>
        <HStack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              w="8px"
              h="8px"
              borderRadius="full"
              bg="blue.400"
              animation={`pulse 1.4s ease-in-out ${i * 0.2}s infinite`}
              sx={{
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                  '50%': { opacity: 1, transform: 'scale(1.2)' },
                },
              }}
            />
          ))}
        </HStack>
      </Box>
    </VStack>
  );
};

export default function ScenarioGenerator() {
  const toast = useToast();
  const scenarioRef = useRef(null);
  const navigate = useNavigate();
  const user = useUser();
  
  // Check if user has enhanced scenario access (state-based for async check)
  const [hasEnhancedAccess, setHasEnhancedAccess] = useState(false);

  // Check if user is system-admin for media generation access
  const isSystemAdmin = user?.roles?.some(role => 
    role === 'system-admin' || 
    role?.key === 'system-admin' ||
    (typeof role === 'string' && role === 'system-admin')
  );

  // Multi-level enhanced scenario access check
  useEffect(() => {
    const checkEnhancedAccess = async () => {
      if (!user?.id) {
        setHasEnhancedAccess(false);
        return;
      }
      
      // Check if user is admin/system-admin (immediate access)
      const isAdmin = user.roles?.some(role => {
        const roleKey = typeof role === 'string' ? role : role?.key;
        return roleKey === 'admin' || roleKey === 'system-admin';
      });
      
      if (isAdmin) {
        setHasEnhancedAccess(true);
        return;
      }
      
      // For non-admins, check multi-level access via backend
      try {
        const result = await api.checkEnhancedScenarioAccess({ userId: user.id });
        setHasEnhancedAccess(result?.hasAccess === true);
      } catch (error) {
        console.error('Error checking enhanced scenario access:', error);
        setHasEnhancedAccess(user?.enhancedScenarioAccess === true); // Fallback to direct field
      }
    };
    
    checkEnhancedAccess();
  }, [user?.id, user?.roles, user?.enhancedScenarioAccess]);
  
  const [{ data: generatedScenario, fetching, error }, generateScenario] = useGlobalAction(api.generateClinicalScenario);
  const [{ data: savedScenario, fetching: savingScenario }, saveForImmersive] = useGlobalAction(api.saveScenarioForImmersive);

  // Scenario state - MUST be declared before hooks that depend on it
  const [scenario, setScenario] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);

  // UI mode state
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  // Form data state - ALL FORM FIELDS
  const [formData, setFormData] = useState({
    trainingLevel: "Paramedic",
    difficulty: 3,
    primaryBodySystem: "AI-Defined",
    natureMechanism: "AI-Defined",
    patientAge: "AI-Defined",
    specifiedAge: "",
    biologicalSex: "AI-Defined",
    barriersToCare: "AI-Defined",
    sceneDescription: "AI-Defined",
    safetyConsideration: "AI-Defined",
    chiefComplaint: "AI-Defined",
    secondaryComplaint: "AI-Defined",
    hpi: "AI-Defined",
    pmhx: "AI-Defined",
    allergies: "AI-Defined",
    currentMedications: "AI-Defined",
    levelOfConsciousness: "AI-Defined",
    airwayStatus: "AI-Defined",
    breathingStatus: "AI-Defined",
    circulatoryStatus: "AI-Defined",
    heent: "AI-Defined",
    neck: "AI-Defined",
    chest: "AI-Defined",
    respiratory: "AI-Defined",
    cardiovascular: "AI-Defined",
    gi: "AI-Defined",
    nervous: "AI-Defined",
    musculoskeletal: "AI-Defined",
    integumentary: "AI-Defined",
    endocrine: "AI-Defined",
    gu: "AI-Defined",
    reproductive: "AI-Defined",
    back: "AI-Defined",
    pelvis: "AI-Defined",
    allowRecovery: false,
    useGoogleAI: false,
    useEnhanced: false,
    verbosity: 'medium',
  });

  // Enhanced mode toggles
  const [useEnhanced, setUseEnhanced] = useState(false);
  const [verbosity, setVerbosity] = useState('medium');

  // Media generation toggles (system-admin only)
  const [generateMedia, setGenerateMedia] = useState(false);
  const [generatePatientImage, setGeneratePatientImage] = useState(true);
  const [generateSceneVideo, setGenerateSceneVideo] = useState(true);
  const [generateClinicalVideos, setGenerateClinicalVideos] = useState(true);

  // Public scenario state
  const [makePublic, setMakePublic] = useState(false);
  const [savedScenarioData, setSavedScenarioData] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [saveIntent, setSaveIntent] = useState(null); // 'immersive' or 'share'

  // Progress tracking state
  // const [progressSessionId, setProgressSessionId] = useState(null);
  // const [streamingStatus, setStreamingStatus] = useState('');
  // const [completedElements, setCompletedElements] = useState({});
  // const [progressEvents, setProgressEvents] = useState([]);
  // const [streamingFailed, setStreamingFailed] = useState(false);

  // Recovery state
  // const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  // const [recoveredSessionId, setRecoveredSessionId] = useState(null);

  // Load scenario from URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (params.toString()) {
      const updates = {};
      
      // Map all possible parameters
      if (params.has('trainingLevel')) updates.trainingLevel = params.get('trainingLevel');
      if (params.has('difficulty')) updates.difficulty = parseInt(params.get('difficulty')) || 3;
      if (params.has('primaryBodySystem')) updates.primaryBodySystem = params.get('primaryBodySystem');
      if (params.has('natureMechanism')) updates.natureMechanism = params.get('natureMechanism');
      if (params.has('patientAge')) updates.patientAge = params.get('patientAge');
      if (params.has('biologicalSex')) updates.biologicalSex = params.get('biologicalSex');
      if (params.has('chiefComplaint')) updates.chiefComplaint = params.get('chiefComplaint');
      if (params.has('secondaryComplaint')) updates.secondaryComplaint = params.get('secondaryComplaint');
      if (params.has('sceneDescription')) updates.sceneDescription = params.get('sceneDescription');
      if (params.has('barriersToCare')) updates.barriersToCare = params.get('barriersToCare');
      if (params.has('safetyConsideration')) updates.safetyConsideration = params.get('safetyConsideration');
      if (params.has('pmhx')) updates.pmhx = params.get('pmhx');
      if (params.has('allergies')) updates.allergies = params.get('allergies');
      if (params.has('currentMedications')) updates.currentMedications = params.get('currentMedications');
      if (params.has('hpi')) updates.hpi = params.get('hpi');
      if (params.has('levelOfConsciousness')) updates.levelOfConsciousness = params.get('levelOfConsciousness');
      if (params.has('airwayStatus')) updates.airwayStatus = params.get('airwayStatus');
      if (params.has('breathingStatus')) updates.breathingStatus = params.get('breathingStatus');
      if (params.has('circulatoryStatus')) updates.circulatoryStatus = params.get('circulatoryStatus');
      if (params.has('heent')) updates.heent = params.get('heent');
      if (params.has('neck')) updates.neck = params.get('neck');
      if (params.has('chest')) updates.chest = params.get('chest');
      if (params.has('respiratory')) updates.respiratory = params.get('respiratory');
      if (params.has('cardiovascular')) updates.cardiovascular = params.get('cardiovascular');
      if (params.has('gi')) updates.gi = params.get('gi');
      if (params.has('nervous')) updates.nervous = params.get('nervous');
      if (params.has('musculoskeletal')) updates.musculoskeletal = params.get('musculoskeletal');
      if (params.has('integumentary')) updates.integumentary = params.get('integumentary');
      if (params.has('endocrine')) updates.endocrine = params.get('endocrine');
      if (params.has('gu')) updates.gu = params.get('gu');
      if (params.has('reproductive')) updates.reproductive = params.get('reproductive');
      if (params.has('back')) updates.back = params.get('back');
      if (params.has('pelvis')) updates.pelvis = params.get('pelvis');
      
      // Apply all updates at once
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
        
        toast({
          title: "Scenario Loaded",
          description: "Pre-filled from existing scenario. Modify as needed.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  }, []); // Empty deps array - run only on mount

  // Set default to enhanced mode if user has access
  useEffect(() => {
    if (hasEnhancedAccess && !formData.useEnhanced) {
      setFormData(prev => ({ ...prev, useEnhanced: true }));
      setUseEnhanced(true);
    }
  }, [hasEnhancedAccess]);

  // Check for active generation on mount (page reload recovery)
  /*
  useEffect(() => {
    const { isActive, sessionId, formData: savedFormData } = checkForActiveGeneration();
    
    if (isActive && sessionId && savedFormData) {
      console.log('Detected active generation after reload:', sessionId);
      
      // Restore form data
      setFormData(savedFormData);
      
      // Resume progress tracking
      setProgressSessionId(sessionId);
      setRecoveredSessionId(sessionId);
      setShowRecoveryBanner(true);
      setStreamingStatus('Recovering generation...');
      setStreamingFailed(false);
      
      toast({
        title: "Generation Resumed",
        description: "Your scenario generation is still in progress.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
    }
  }, []); // Run only on mount
  */

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const inputBg = useColorModeValue("white", "gray.700");
  const focusBorderColor = "blue.500";

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAIDefinedClick = (field) => {
    if (formData[field] === "AI-Defined") {
      setFormData((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const clearForm = () => {
    setFormData({
      trainingLevel: "Paramedic",
      difficulty: 3,
      primaryBodySystem: "AI-Defined",
      natureMechanism: "AI-Defined",
      patientAge: "AI-Defined",
      specifiedAge: "",
      biologicalSex: "AI-Defined",
      barriersToCare: "AI-Defined",
      sceneDescription: "AI-Defined",
      safetyConsideration: "AI-Defined",
      chiefComplaint: "AI-Defined",
      secondaryComplaint: "AI-Defined",
      hpi: "AI-Defined",
      pmhx: "AI-Defined",
      allergies: "AI-Defined",
      currentMedications: "AI-Defined",
      levelOfConsciousness: "AI-Defined",
      airwayStatus: "AI-Defined",
      breathingStatus: "AI-Defined",
      circulatoryStatus: "AI-Defined",
      heent: "AI-Defined",
      respiratory: "AI-Defined",
      cardiovascular: "AI-Defined",
      gi: "AI-Defined",
      nervous: "AI-Defined",
      musculoskeletal: "AI-Defined",
      integumentary: "AI-Defined",
      endocrine: "AI-Defined",
      gu: "AI-Defined",
      reproductive: "AI-Defined",
      allowRecovery: false,
      useGoogleAI: false,
      useEnhanced: hasEnhancedAccess ? true : false,
      verbosity: 'medium',
    });
    setScenario(null);
    setSavedScenarioData(null);
  };

  const handleGenerate = async () => {
    try {
      // Start the actual generation
      await generateScenario({
        ...formData,
        patientAge: formData.patientAge === "Specify" ? formData.specifiedAge : formData.patientAge,
        allowRecovery: formData.allowRecovery,
        isPublic: makePublic,
        useEnhanced: formData.useEnhanced || false,
        verbosity: formData.verbosity || 'medium',
        useGoogleAI: formData.useGoogleAI || false,
        // Add media generation parameters
        generateMedia: isSystemAdmin ? generateMedia : false,
        generatePatientImage: isSystemAdmin && generateMedia ? generatePatientImage : false,
        generateSceneVideo: isSystemAdmin && generateMedia ? generateSceneVideo : false,
        generateClinicalVideos: isSystemAdmin && generateMedia ? generateClinicalVideos : false,
      });
    } catch (err) {
      toast({
        title: "Generation Failed",
        description: err.message || "Failed to start scenario generation.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Poll for progress updates
  /*
  useEffect(() => {
    if (!progressSessionId || !fetching) return;
    
    let failureCount = 0;
    const maxFailures = 5; // After 5 failed polls, switch to fallback
    
    const pollInterval = setInterval(async () => {
      try {
        const progress = await api.scenarioGenerationProgress.findFirst({
          filter: { sessionId: { equals: progressSessionId } },
          select: {
            status: true,
            currentMessage: true,
            completedElements: true,
            progressEvents: true,
            generatedScenario: true
          }
        });
        
        if (progress) {
          // Reset failure count on success
          failureCount = 0;
          setStreamingFailed(false);
          
          setStreamingStatus(progress.currentMessage || '');
          setCompletedElements(progress.completedElements || {});
          setProgressEvents(progress.progressEvents || []);
          
          // If complete, stop polling
          if (progress.status === 'complete' && progress.generatedScenario) {
            clearInterval(pollInterval);
            setProgressSessionId(null);
          }
        } else {
          // No progress record found yet, increment failure count
          failureCount++;
          if (failureCount >= maxFailures) {
            console.warn('Streaming progress unavailable, using fallback loader');
            setStreamingFailed(true);
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
        failureCount++;
        if (failureCount >= maxFailures) {
          console.warn('Streaming progress failed, using fallback loader');
          setStreamingFailed(true);
        }
      }
    }, 250); // Poll every 250ms
    
    return () => clearInterval(pollInterval);
  }, [progressSessionId, fetching]);
  */

  // Watch for scenario generation completion
  useEffect(() => {
    if (generatedScenario && !fetching && !error) {
      console.log('=== SCENARIO GENERATION DEBUG ===');
      console.log('Full result object:', generatedScenario);
      console.log('Scenario object:', generatedScenario.scenario);
      console.log('Has patientInfo?', !!generatedScenario.scenario?.patientInfo);
      console.log('Has sceneSetup?', !!generatedScenario.scenario?.sceneSetup);
      console.log('Has history?', !!generatedScenario.scenario?.history);
      console.log('Has primaryAssessment?', !!generatedScenario.scenario?.primaryAssessment);
      console.log('Has secondaryAssessment?', !!generatedScenario.scenario?.secondaryAssessment);
      console.log('Has vitalSigns?', !!generatedScenario.scenario?.vitalSigns);
      console.log('Has interventions?', !!generatedScenario.scenario?.interventions);
      console.log('Has learningObjectives?', !!generatedScenario.scenario?.learningObjectives);
      console.log('=== END DEBUG ===');
    
      setScenario(generatedScenario.scenario);
      setSavedScenarioData(null); // Clear saved scenario data when generating new scenario
      
      toast({
        title: "Scenario Generated",
        description: "Your clinical scenario has been successfully generated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    
      setTimeout(() => {
        scenarioRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [generatedScenario, fetching, error]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Generation Failed",
        description: error.message || "An error occurred while generating the scenario.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error]);

  // Watch for scenario save completion for immersive mode
  useEffect(() => {
    if (savedScenario && !savingScenario && saveIntent === 'immersive') {
      setSaveIntent(null);
      toast({
        title: "Scenario Saved",
        description: "Redirecting to Immersive Scenario Generator...",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      
      // Navigate to immersive generator with scenario ID
      setTimeout(() => {
        navigate(`/immersive-scenario-generator?scenarioId=${savedScenario.id}`);
      }, 500);
    }
  }, [savedScenario, savingScenario, saveIntent, navigate]);

  // Watch for scenario save completion for sharing
  useEffect(() => {
    if (savedScenario && !savingScenario && saveIntent === 'share') {
      setSaveIntent(null);
      // Save completed for sharing - store it and open modal
      setSavedScenarioData(savedScenario);
      setIsShareModalOpen(true);
    }
  }, [savedScenario, savingScenario, saveIntent]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    try {
      toast({
        title: "Generating PDF",
        description: "Creating your PDF document...",
        status: "info",
        duration: 2000,
        isClosable: true,
      });

      if (!scenario) {
        throw new Error('No scenario to export');
      }

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const usableWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper function to add text with word wrap
      const addText = (text, x, y, maxWidth, fontSize = 10) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text || '', maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * fontSize * 0.35); // Return new Y position
      };

      // Helper function to draw a box
      const drawBox = (x, y, width, height, title) => {
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(x, y, width, height);
        if (title) {
          pdf.setFillColor(240, 240, 240);
          pdf.rect(x, y, width, 6, 'F');
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(title, x + 2, y + 4);
          pdf.setFont('helvetica', 'normal');
        }
      };

      // Helper to check if we need a new page
      const checkNewPage = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // === PAGE 1 ===
      
      // Title Header
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Clinical Scenario', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      // Training Level & Difficulty badges
      pdf.setFontSize(11);
      const trainingLevel = scenario.ScenarioMetadata?.TrainingLevel || scenario.trainingLevel || 'Paramedic';
      const difficulty = scenario.ScenarioMetadata?.Difficulty || scenario.difficultyLevel || 'Intermediate';
      pdf.text(`${trainingLevel} | Difficulty: ${difficulty}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      pdf.setFont('helvetica', 'normal');

      // Scene Information
      const sceneData = {
        location: scenario.SceneSetup?.Location || scenario.sceneSetup?.location,
        timeOfDay: scenario.SceneSetup?.TimeOfDay || scenario.sceneSetup?.timeOfDay,
        weather: scenario.SceneSetup?.Weather || scenario.sceneSetup?.weather,
        dispatch: scenario.SceneSetup?.DispatchInformation || scenario.sceneSetup?.dispatchInfo,
        safety: scenario.SceneSetup?.SafetyIssues || scenario.sceneSetup?.safetyIssues
      };

      if (sceneData.location || sceneData.dispatch) {
        drawBox(margin, yPos, usableWidth, 25, 'Scene Information');
        let boxY = yPos + 8;
        pdf.setFontSize(9);
        if (sceneData.location) boxY = addText(sceneData.location, margin + 2, boxY, usableWidth - 4, 9);
        if (sceneData.timeOfDay || sceneData.weather) {
          boxY = addText(`${sceneData.timeOfDay || ''} ${sceneData.weather || ''}`.trim(), margin + 2, boxY, usableWidth - 4, 8);
        }
        if (sceneData.dispatch) boxY = addText(`Dispatch: ${sceneData.dispatch}`, margin + 2, boxY, usableWidth - 4, 9);
        yPos += 27;
      }

      // Patient Information
      const patientData = {
        name: scenario.PatientDemographics?.FullName || scenario.patientInfo?.name,
        age: scenario.PatientDemographics?.Age || scenario.patientInfo?.age,
        sex: scenario.PatientDemographics?.Sex || scenario.patientInfo?.sex,
        weight: scenario.PatientDemographics?.Weight || scenario.patientInfo?.weight
      };

      drawBox(margin, yPos, usableWidth, 12, 'Patient Information');
      pdf.setFontSize(9);
      pdf.text(`${patientData.age || 'Unknown'} ${patientData.sex || ''} ${patientData.weight || ''}`.trim(), margin + 2, yPos + 9);
      yPos += 14;

      // Chief Complaint
      if (scenario.chiefComplaint) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Chief Complaint:', margin, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos = addText(scenario.chiefComplaint, margin, yPos + 4, usableWidth, 10);
        yPos += 3;
      }

      // History (condensed)
      const history = {
        hpi: scenario.History?.HPI || scenario.history?.hpi,
        pmhx: scenario.History?.PMHx || scenario.history?.pmhx,
        medications: scenario.History?.Medications || scenario.history?.medications,
        allergies: scenario.History?.Allergies || scenario.history?.allergies
      };

      if (history.hpi || history.pmhx) {
        drawBox(margin, yPos, usableWidth, 20, 'History');
        let histY = yPos + 8;
        pdf.setFontSize(8);
        if (history.hpi) {
          const hpiText = typeof history.hpi === 'string' ? history.hpi : JSON.stringify(history.hpi);
          histY = addText(`HPI: ${hpiText.substring(0, 150)}...`, margin + 2, histY, usableWidth - 4, 8);
        }
        if (history.pmhx) histY = addText(`PMHx: ${history.pmhx}`, margin + 2, histY, usableWidth - 4, 8);
        if (history.allergies) histY = addText(`Allergies: ${history.allergies}`, margin + 2, histY, usableWidth - 4, 8);
        yPos += 22;
      }

      // Primary Assessment
      const primaryAssessment = {
        mentalStatus: scenario.AssessmentFindings?.PrimaryAssessment?.MentalStatus || scenario.primaryAssessment?.mentalStatus,
        airway: scenario.AssessmentFindings?.PrimaryAssessment?.Airway || scenario.primaryAssessment?.airway,
        breathing: scenario.AssessmentFindings?.PrimaryAssessment?.Breathing || scenario.primaryAssessment?.breathing,
        circulation: scenario.AssessmentFindings?.PrimaryAssessment?.Circulation || scenario.primaryAssessment?.circulation
      };

      if (primaryAssessment.airway || primaryAssessment.breathing || primaryAssessment.circulation) {
        drawBox(margin, yPos, usableWidth, 15, 'Primary Assessment (ABC)');
        let abcY = yPos + 8;
        pdf.setFontSize(8);
        if (primaryAssessment.airway) abcY = addText(`A: ${primaryAssessment.airway}`, margin + 2, abcY, usableWidth - 4, 8);
        if (primaryAssessment.breathing) abcY = addText(`B: ${primaryAssessment.breathing}`, margin + 2, abcY, usableWidth - 4, 8);
        if (primaryAssessment.circulation) abcY = addText(`C: ${primaryAssessment.circulation}`, margin + 2, abcY, usableWidth - 4, 8);
        yPos += 17;
      }

      // Initial Vital Signs (compact grid)
      const vitals = scenario.VitalSigns?.Initial || scenario.vitalSigns?.initial;
      if (vitals) {
        const vitalData = {
          hr: vitals.HR || vitals.hr,
          bp: vitals.BP || vitals.bp,
          rr: vitals.RR || vitals.rr,
          spo2: vitals.SpO2 || vitals.spo2,
          temp: vitals.Temp || vitals.temp,
          glucose: vitals.BGL || vitals.bloodGlucose
        };

        drawBox(margin, yPos, usableWidth, 12, 'Initial Vital Signs');
        pdf.setFontSize(9);
        const vitalStr = [
          vitalData.hr ? `HR: ${vitalData.hr}` : '',
          vitalData.bp ? `BP: ${vitalData.bp}` : '',
          vitalData.rr ? `RR: ${vitalData.rr}` : '',
          vitalData.spo2 ? `SpO2: ${vitalData.spo2}` : '',
          vitalData.temp ? `Temp: ${vitalData.temp}` : '',
          vitalData.glucose ? `BGL: ${vitalData.glucose}` : ''
        ].filter(v => v).join(' | ');
        pdf.text(vitalStr, margin + 2, yPos + 9);
        yPos += 14;
      }

      // Interventions (bulleted list)
      let interventions = scenario.Interventions || scenario.interventions;
      if (Array.isArray(interventions)) {
        interventions = { LifeSaving: [], Secondary: interventions, Optional: [] };
      }

      if (interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0)) {
        checkNewPage(30);
        drawBox(margin, yPos, usableWidth, 25, 'Interventions');
        let intY = yPos + 8;
        pdf.setFontSize(8);
        
        const allInterventions = [
          ...(interventions.LifeSaving || []).map(i => `★ ${i}`),
          ...(interventions.Secondary || []).map(i => `• ${i}`)
        ];
        
        allInterventions.slice(0, 8).forEach(int => {
          const lines = pdf.splitTextToSize(int, usableWidth - 4);
          pdf.text(lines[0], margin + 2, intY);
          intY += 3;
        });
        yPos += 27;
      }

      // === PAGE 2 ===
      pdf.addPage();
      yPos = margin;

      // Trending Vitals
      const trendingVitals = scenario.VitalSigns?.Trending || scenario.vitalSigns?.trending;
      if (trendingVitals && trendingVitals.length > 0) {
        drawBox(margin, yPos, usableWidth, 20, 'Trending Vital Signs');
        let trendY = yPos + 8;
        pdf.setFontSize(8);
        trendingVitals.slice(0, 3).forEach((vSet, idx) => {
          const time = vSet.Time || vSet.time || `Set ${idx + 1}`;
          const hr = vSet.HR || vSet.hr;
          const bp = vSet.BP || vSet.bp;
          const rr = vSet.RR || vSet.rr;
          const spo2 = vSet.SpO2 || vSet.spo2;
          const trendStr = `${time}: HR ${hr || '?'} | BP ${bp || '?'} | RR ${rr || '?'} | SpO2 ${spo2 || '?'}`;
          pdf.text(trendStr, margin + 2, trendY);
          trendY += 3.5;
        });
        yPos += 22;
      }

      // Expected Outcome
      const outcome = {
        finalCondition: scenario.ExpectedOutcome?.FinalCondition || scenario.expectedOutcome?.finalCondition,
        disposition: scenario.ExpectedOutcome?.Disposition || scenario.expectedOutcome?.disposition
      };

      if (outcome.finalCondition || outcome.disposition) {
        drawBox(margin, yPos, usableWidth, 18, 'Expected Outcome');
        let outY = yPos + 8;
        pdf.setFontSize(9);
        if (outcome.finalCondition) outY = addText(outcome.finalCondition, margin + 2, outY, usableWidth - 4, 9);
        if (outcome.disposition) outY = addText(`Disposition: ${outcome.disposition}`, margin + 2, outY, usableWidth - 4, 8);
        yPos += 20;
      }

      // Critical Actions
      const criticalActions = scenario.EducationalComponents?.CriticalActions || scenario.criticalActions;
      if (criticalActions && criticalActions.length > 0) {
        drawBox(margin, yPos, usableWidth, 25, 'Critical Actions');
        let critY = yPos + 8;
        pdf.setFontSize(8);
        criticalActions.slice(0, 6).forEach((action, idx) => {
          const lines = pdf.splitTextToSize(`${idx + 1}. ${action}`, usableWidth - 4);
          pdf.text(lines[0], margin + 2, critY);
          critY += 3.5;
        });
        yPos += 27;
      }

      // Common Pitfalls
      const pitfalls = scenario.EducationalComponents?.CommonPitfalls || scenario.commonPitfalls;
      if (pitfalls && pitfalls.length > 0) {
        checkNewPage(20);
        drawBox(margin, yPos, usableWidth, 20, 'Common Pitfalls');
        let pitY = yPos + 8;
        pdf.setFontSize(8);
        pitfalls.slice(0, 5).forEach(pitfall => {
          const lines = pdf.splitTextToSize(`• ${pitfall}`, usableWidth - 4);
          pdf.text(lines[0], margin + 2, pitY);
          pitY += 3.5;
        });
        yPos += 22;
      }

      // Differential Diagnosis
      const differentials = scenario.EducationalComponents?.DifferentialDiagnosis || scenario.differentialDiagnosis;
      if (differentials && differentials.length > 0) {
        checkNewPage(15);
        drawBox(margin, yPos, usableWidth, 15, 'Differential Diagnosis');
        let diffY = yPos + 8;
        pdf.setFontSize(8);
        differentials.slice(0, 5).forEach(diagnosis => {
          pdf.text(`• ${diagnosis}`, margin + 2, diffY);
          diffY += 3.5;
        });
        yPos += 17;
      }

      // Generate filename and save
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `clinical-scenario-${trainingLevel.toLowerCase()}-${timestamp}.pdf`;
      pdf.save(filename);

      toast({
        title: "PDF Downloaded",
        description: "Your scenario has been saved as a clean, condensed PDF.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('PDF generation error:', err);
      toast({
        title: "PDF Generation Failed",
        description: err.message || "Failed to generate PDF. Please try printing instead.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCreateImmersive = async () => {
    try {
      setSaveIntent('immersive');
      await saveForImmersive({
        scenario: scenario,
        userId: user.id,
        institution: user.institution || 'Unknown',
        isPublic: makePublic
      });
    } catch (err) {
      setSaveIntent(null);
      toast({
        title: "Save Failed",
        description: err.message || "Failed to save scenario. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleShareScenario = async () => {
    if (!scenario) {
      toast({
        title: "No scenario to share",
        description: "Please generate a scenario first.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // If scenario is already saved, just open the modal
    if (savedScenarioData?.id) {
      setIsShareModalOpen(true);
      return;
    }

    // Otherwise save it first - the modal will open via useEffect
    try {
      setSaveIntent('share');
      await saveForImmersive({
        scenario: scenario,
        userId: user.id,
        institution: user.institution || 'Unknown',
        isPublic: makePublic
      });
    } catch (err) {
      setSaveIntent(null);
      toast({
        title: "Save Failed",
        description: "Failed to save scenario before sharing. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box bg="gray.900" minH="100vh" py={12}>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            body {
              background: white !important;
            }
            .scenario-container {
              background: white !important;
              box-shadow: none !important;
              border: none !important;
            }
            .chakra-container {
              max-width: 100% !important;
            }
            .chakra-card {
              background: white !important;
              box-shadow: none !important;
              border: 1px solid #e2e8f0 !important;
            }
            * {
              color: black !important;
            }
            @page {
              margin: 0.75in;
              size: letter;
            }
          }
        `}
      </style>
      <Container maxW="7xl">
        {/* Recovery banner for resumed generation */}
        {/* 
        {showRecoveryBanner && progressSessionId && (
          <Box
            className="no-print"
            position="fixed"
            top={4}
            left="50%"
            transform="translateX(-50%)"
            zIndex={9998}
            maxW="600px"
            w="90%"
          >
            <Box
              bg="blue.900"
              borderRadius="lg"
              borderWidth="2px"
              borderColor="blue.500"
              p={4}
              shadow="lg"
            >
              <HStack spacing={3}>
                <Spinner size="sm" color="blue.400" />
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontSize="sm" fontWeight="bold" color="blue.300">
                    Generation Resumed
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    Your scenario generation was recovered after page reload
                  </Text>
                </VStack>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => {
                    clearGenerationState();
                    setShowRecoveryBanner(false);
                    setProgressSessionId(null);
                  }}
                >
                  Dismiss
                </Button>
              </HStack>
            </Box>
          </Box>
        )}
        */}

        {fetching && (
          <Box
            className="no-print"
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
            p={4}
          >
            <Box bg="gray.900" p={6} borderRadius="lg" borderWidth="2px" borderColor="blue.500" maxW="800px" w="full">
              <HStack spacing={3} mb={4}>
                <Spinner size="md" color="blue.400" thickness="3px" />
                <Text fontSize="xl" fontWeight="bold" color="blue.300">
                  Generating Clinical Scenario
                </Text>
              </HStack>
              
              {/* Show simple loading animation */}
              <SimpleFallbackLoader />
            </Box>
          </Box>
        )}

        <VStack spacing={10} align="stretch">
          {/* Header */}
          <Box className="no-print">
            <HStack spacing={4} mb={4}>
              <Button
                leftIcon={<ArrowBackIcon />}
                colorScheme="gray"
                variant="ghost"
                onClick={() => navigate('/my-scenarios')}
                size="lg"
              >
                Back to Scenarios
              </Button>
            </HStack>
            <Box
              textAlign="center"
              borderBottom="3px solid"
              borderColor="blue.500"
              pb={6}
            >
              <Heading
                size="2xl"
                color="white"
                fontWeight="bold"
                mb={3}
              >
                Clinical Scenario Generator
              </Heading>
              <Text color="gray.400" fontSize="lg" maxW="2xl" mx="auto">
                Generate realistic, customizable clinical scenarios for EMS training and education
              </Text>
              <HStack justify="center" mt={6} spacing={3}>
                <Text fontWeight="medium" color="gray.300">Simple</Text>
                <Switch
                  size="lg"
                  isChecked={isAdvancedMode}
                  onChange={(e) => setIsAdvancedMode(e.target.checked)}
                  colorScheme="blue"
                />
                <Text fontWeight="medium" color="gray.300">Advanced</Text>
              </HStack>
            </Box>
          </Box>

          {!isAdvancedMode ? (
            <Card
              className="no-print"
              bg={cardBg}
              borderRadius="xl"
              shadow="2xl"
              overflow="hidden"
              maxW="700px"
              mx="auto"
            >
              <CardBody p={10}>
                <VStack spacing={8} align="stretch">
                  <Heading size="lg" color="blue.500" fontWeight="bold" textAlign="center">
                    Quick Scenario Setup
                  </Heading>
                  <Divider borderColor="blue.500" />

                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold" color="gray.300">Training Level</FormLabel>
                    <Select
                      value={formData.trainingLevel}
                      onChange={(e) => handleInputChange("trainingLevel", e.target.value)}
                      bg={inputBg}
                      borderRadius="lg"
                      focusBorderColor={focusBorderColor}
                      size="lg"
                    >
                      {TRAINING_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {hasEnhancedAccess && (
                    <>
                      <FormControl>
                        <FormLabel fontWeight="semibold" color="gray.300">Scenario Quality</FormLabel>
                        <HStack spacing={4}>
                          <Button
                            size="md"
                            onClick={() => {
                              setUseEnhanced(false);
                              handleInputChange('useEnhanced', false);
                            }}
                            colorScheme={!useEnhanced ? 'blue' : 'gray'}
                            variant={!useEnhanced ? 'solid' : 'outline'}
                          >
                            Basic (Faster)
                          </Button>
                          <Button
                            size="md"
                            onClick={() => {
                              setUseEnhanced(true);
                              handleInputChange('useEnhanced', true);
                            }}
                            colorScheme={useEnhanced ? 'purple' : 'gray'}
                            variant={useEnhanced ? 'solid' : 'outline'}
                          >
                            Enhanced (Slower)
                          </Button>
                        </HStack>
                        <FormHelperText color="gray.400">
                          {useEnhanced 
                            ? 'Enhanced scenarios use GPT-5 for maximum clinical detail and realism. Generation may take 2-5 minutes.'
                            : 'Basic scenarios use GPT-4o for quick generation (typically under 1 minute).'}
                        </FormHelperText>
                      </FormControl>

                      {useEnhanced && (
                        <FormControl>
                          <FormLabel fontWeight="semibold" color="gray.300">Scenario Detail Level</FormLabel>
                          <VStack align="stretch" spacing={2}>
                            <HStack spacing={4}>
                              <Button
                                size="sm"
                                flex={1}
                                onClick={() => {
                                  setVerbosity('low');
                                  handleInputChange('verbosity', 'low');
                                }}
                                colorScheme={verbosity === 'low' ? 'purple' : 'gray'}
                                variant={verbosity === 'low' ? 'solid' : 'outline'}
                              >
                                Concise
                              </Button>
                              <Button
                                size="sm"
                                flex={1}
                                onClick={() => {
                                  setVerbosity('medium');
                                  handleInputChange('verbosity', 'medium');
                                }}
                                colorScheme={verbosity === 'medium' ? 'purple' : 'gray'}
                                variant={verbosity === 'medium' ? 'solid' : 'outline'}
                              >
                                Standard (Recommended)
                              </Button>
                            </HStack>
                            <FormHelperText fontSize="xs" color="gray.400">
                              Concise mode provides focused clinical details. Standard mode includes comprehensive context and teaching points.
                            </FormHelperText>
                          </VStack>
                        </FormControl>
                      )}

                      {useEnhanced && (
                        <Box p={3} bg="purple.900" borderRadius="md" borderWidth="1px" borderColor="purple.500">
                          <HStack spacing={2}>
                            <Badge colorScheme="purple">NOTICE</Badge>
                            <Text fontSize="sm" color="purple.100">
                              Enhanced scenarios with higher detail may take up to 5 minutes to generate. Please be patient.
                            </Text>
                          </HStack>
                        </Box>
                      )}
                    </>
                  )}

                  {/* Media Generation Options (System Admin Only) */}
                  {isSystemAdmin && (
                    <Card 
                      bg={cardBg} 
                      borderColor={borderColor} 
                      borderWidth="1px"
                      p={4}
                    >
                      <VStack align="stretch" spacing={4}>
                        <HStack justify="space-between" align="center">
                          <VStack align="start" spacing={1}>
                            <HStack>
                              <Text fontWeight="bold" fontSize="lg">Media Generation</Text>
                              <Badge colorScheme="red" fontSize="xs">SYSTEM ADMIN</Badge>
                            </HStack>
                            <Text fontSize="sm" color="gray.400">
                              Generate images and videos for this scenario using Google AI
                            </Text>
                          </VStack>
                          <Switch
                            size="lg"
                            isChecked={generateMedia}
                            onChange={(e) => setGenerateMedia(e.target.checked)}
                            colorScheme="purple"
                          />
                        </HStack>

                        {generateMedia && (
                          <VStack align="stretch" spacing={3} pl={4} borderLeft="2px" borderColor="purple.500">
                            <FormControl>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <FormLabel mb={0}>Patient Still Image</FormLabel>
                                  <FormHelperText mt={0} fontSize="xs">
                                    Generate photorealistic patient image using Gemini 2.5 Flash
                                  </FormHelperText>
                                </VStack>
                                <Checkbox
                                  isChecked={generatePatientImage}
                                  onChange={(e) => setGeneratePatientImage(e.target.checked)}
                                  colorScheme="purple"
                                />
                              </HStack>
                            </FormControl>

                            <FormControl>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <FormLabel mb={0}>Scene Arrival Video</FormLabel>
                                  <FormHelperText mt={0} fontSize="xs">
                                    Generate first-person POV arrival scene video using Veo
                                  </FormHelperText>
                                </VStack>
                                <Checkbox
                                  isChecked={generateSceneVideo}
                                  onChange={(e) => setGenerateSceneVideo(e.target.checked)}
                                  colorScheme="purple"
                                />
                              </HStack>
                            </FormControl>

                            <FormControl>
                              <HStack justify="space-between">
                                <VStack align="start" spacing={0}>
                                  <FormLabel mb={0}>Clinical Progression Videos</FormLabel>
                                  <FormHelperText mt={0} fontSize="xs">
                                    Generate deterioration and improvement videos using Veo
                                  </FormHelperText>
                                </VStack>
                                <Checkbox
                                  isChecked={generateClinicalVideos}
                                  onChange={(e) => setGenerateClinicalVideos(e.target.checked)}
                                  colorScheme="purple"
                                />
                              </HStack>
                            </FormControl>

                            <Box p={3} bg="purple.900" borderRadius="md">
                              <Text fontSize="xs" color="purple.200">
                                <strong>Note:</strong> Media generation happens in the background after scenario creation.
                                Videos typically take 1-2 minutes to generate. Check the scenario details to see media status.
                              </Text>
                            </Box>
                          </VStack>
                        )}
                      </VStack>
                    </Card>
                  )}

                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold" color="gray.300">Difficulty Level: {formData.difficulty}</FormLabel>
                    <Box pt={2} pb={8}>
                      <Slider
                        value={formData.difficulty}
                        onChange={(val) => handleInputChange("difficulty", val)}
                        min={1}
                        max={5}
                        step={1}
                      >
                        <SliderMark value={1} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                          1
                        </SliderMark>
                        <SliderMark value={2} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                          2
                        </SliderMark>
                        <SliderMark value={3} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                          3
                        </SliderMark>
                        <SliderMark value={4} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                          4
                        </SliderMark>
                        <SliderMark value={5} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                          5
                        </SliderMark>
                        <SliderTrack bg="gray.600" h={2}>
                          <SliderFilledTrack bg={DIFFICULTY_COLORS[formData.difficulty]} />
                        </SliderTrack>
                        <SliderThumb boxSize={7} bg={DIFFICULTY_COLORS[formData.difficulty]} _hover={{ transform: "scale(1.2)" }} transition="all 0.2s" />
                      </Slider>
                    </Box>
                    <Text fontSize="sm" color="gray.400" fontStyle="italic">
                      {DIFFICULTY_DESCRIPTIONS[formData.difficulty]}
                    </Text>
                  </FormControl>

                  {formData.difficulty === 5 && (
                    <FormControl>
                      <Checkbox 
                        isChecked={formData.allowRecovery}
                        onChange={(e) => handleInputChange('allowRecovery', e.target.checked)}
                        colorScheme="blue"
                        size="lg"
                      >
                        <Text fontWeight="semibold" color="gray.300">Allow Patient Recovery</Text>
                      </Checkbox>
                      <Text fontSize="sm" color="gray.400" fontStyle="italic" mt={2}>
                        For difficulty 5 scenarios, choose whether the patient should have a chance to recover with proper interventions, or if this is an unrecoverable/terminal case.
                      </Text>
                    </FormControl>
                  )}

                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold" color="gray.300">What would you like to practice?</FormLabel>
                    <FormHelperText mb={2} color="gray.400">
                      Describe the situation, condition, or skill you want to simulate. Be as specific or general as you like.
                    </FormHelperText>
                    <Textarea
                      value={formData.natureMechanism === "AI-Defined" ? "" : formData.natureMechanism}
                      onChange={(e) => handleInputChange("natureMechanism", e.target.value || "AI-Defined")}
                      placeholder="Example: A pediatric patient with an asthma exacerbation requiring advanced airway management..."
                      bg={inputBg}
                      borderRadius="lg"
                      focusBorderColor={focusBorderColor}
                      size="lg"
                      rows={8}
                      resize="vertical"
                      _placeholder={{ color: "gray.500" }}
                    />
                  </FormControl>

                  <FormControl>
                    <Checkbox 
                      isChecked={makePublic}
                      onChange={(e) => setMakePublic(e.target.checked)}
                      colorScheme="green"
                      size="lg"
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" color="gray.300">Make this scenario public</Text>
                        <Text fontSize="xs" color="gray.500">Allow other institutions to access this scenario</Text>
                      </VStack>
                    </Checkbox>
                    <FormHelperText color="yellow.300" fontSize="sm" mt={2}>
                      ⚠️ Public scenarios may be used by other institutions for training. Ensure all content is accurate and appropriate.
                    </FormHelperText>
                  </FormControl>

                  <Divider borderColor="blue.500" />

                  <HStack spacing={4} justify="center">
                    <Button
                      colorScheme="green"
                      bg="#00aa00"
                      size="lg"
                      px={12}
                      onClick={handleGenerate}
                      isLoading={fetching}
                      loadingText="Generating Scenario"
                      width="full"
                    >
                      Generate Scenario
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      size="md"
                      onClick={clearForm}
                    >
                      Clear Form
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            <Grid className="no-print" templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
            {/* Setup Section */}
            <GridItem>
              <Card
                bg={cardBg}
                borderRadius="xl"
                shadow="2xl"
                overflow="hidden"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "3xl" }}
              >
                <CardBody p={10}>
                  <VStack spacing={8} align="stretch">
                    <Heading size="lg" color="blue.500" fontWeight="bold">
                      Setup
                    </Heading>
                    <Divider borderColor="blue.500" />

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Student Training Level</FormLabel>
                      <Select
                        value={formData.trainingLevel}
                        onChange={(e) => handleInputChange("trainingLevel", e.target.value)}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                      >
                        {TRAINING_LEVELS.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Difficulty Level: {formData.difficulty}</FormLabel>
                      <Box pt={2} pb={8}>
                        <Slider
                          value={formData.difficulty}
                          onChange={(val) => handleInputChange("difficulty", val)}
                          min={1}
                          max={5}
                          step={1}
                        >
                          <SliderMark value={1} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                            1
                          </SliderMark>
                          <SliderMark value={2} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                            2
                          </SliderMark>
                          <SliderMark value={3} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                            3
                          </SliderMark>
                          <SliderMark value={4} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                            4
                          </SliderMark>
                          <SliderMark value={5} mt={3} ml={-2} fontSize="sm" color="gray.400" fontWeight="medium">
                            5
                          </SliderMark>
                          <SliderTrack bg="gray.600" h={2}>
                            <SliderFilledTrack bg={DIFFICULTY_COLORS[formData.difficulty]} />
                          </SliderTrack>
                          <SliderThumb boxSize={7} bg={DIFFICULTY_COLORS[formData.difficulty]} _hover={{ transform: "scale(1.2)" }} transition="all 0.2s" />
                        </Slider>
                      </Box>
                      <Text fontSize="sm" color="gray.400" fontStyle="italic">
                        {DIFFICULTY_DESCRIPTIONS[formData.difficulty]}
                      </Text>
                    </FormControl>

                    {formData.difficulty === 5 && (
                      <FormControl>
                        <Checkbox 
                          isChecked={formData.allowRecovery}
                          onChange={(e) => handleInputChange('allowRecovery', e.target.checked)}
                          colorScheme="blue"
                          size="lg"
                        >
                          <Text fontWeight="semibold" color="gray.300">Allow Patient Recovery</Text>
                        </Checkbox>
                        <Text fontSize="sm" color="gray.400" fontStyle="italic" mt={2}>
                          For difficulty 5 scenarios, choose whether the patient should have a chance to recover with proper interventions, or if this is an unrecoverable/terminal case.
                        </Text>
                      </FormControl>
                    )}

                    <FormControl>
                      <Checkbox 
                        isChecked={makePublic}
                        onChange={(e) => setMakePublic(e.target.checked)}
                        colorScheme="green"
                        size="lg"
                      >
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="semibold" color="gray.300">Make this scenario public</Text>
                          <Text fontSize="xs" color="gray.500">Allow other institutions to access this scenario</Text>
                        </VStack>
                      </Checkbox>
                      <FormHelperText color="yellow.300" fontSize="sm" mt={2}>
                        ⚠️ Public scenarios may be used by other institutions for training. Ensure all content is accurate and appropriate.
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Primary Body System</FormLabel>
                      <Select
                        value={formData.primaryBodySystem}
                        onChange={(e) => handleInputChange("primaryBodySystem", e.target.value)}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                      >
                        {BODY_SYSTEMS.map((system) => (
                          <option key={system} value={system}>
                            {system}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Nature/Mechanism</FormLabel>
                      <Textarea
                        value={formData.natureMechanism}
                        onChange={(e) => handleInputChange("natureMechanism", e.target.value)}
                        onClick={() => handleAIDefinedClick("natureMechanism")}
                        placeholder="Describe the nature of call or mechanism of injury in detail (several sentences)... Click 'AI-Defined' text to reset."
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        rows={6}
                        minHeight="150px"
                        resize="vertical"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Patient Age</FormLabel>
                      <Select
                        value={formData.patientAge}
                        onChange={(e) => handleInputChange("patientAge", e.target.value)}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                      >
                        {PATIENT_AGES.map((age) => (
                          <option key={age} value={age}>
                            {age}
                          </option>
                        ))}
                      </Select>
                      {formData.patientAge === "Specify" && (
                        <Input
                          mt={3}
                          placeholder="Enter specific age"
                          value={formData.specifiedAge}
                          onChange={(e) => handleInputChange("specifiedAge", e.target.value)}
                          bg={inputBg}
                          borderRadius="lg"
                          focusBorderColor={focusBorderColor}
                          size="lg"
                          _placeholder={{ color: "gray.500" }}
                        />
                      )}
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Biological Sex</FormLabel>
                      <Select
                        value={formData.biologicalSex}
                        onChange={(e) => handleInputChange("biologicalSex", e.target.value)}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                      >
                        {BIOLOGICAL_SEX.map((sex) => (
                          <option key={sex} value={sex}>
                            {sex}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Primary Assessment Section */}
            <GridItem>
              <Card
                bg={cardBg}
                borderRadius="xl"
                shadow="2xl"
                overflow="hidden"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "3xl" }}
              >
                <CardBody p={10}>
                  <VStack spacing={8} align="stretch" pb={8}>
                    <Heading size="lg" color="blue.500" fontWeight="bold">
                      Primary Assessment
                    </Heading>
                    <Divider borderColor="blue.500" />

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Level of Consciousness</FormLabel>
                      <Textarea
                        value={formData.levelOfConsciousness}
                        onChange={(e) => handleInputChange("levelOfConsciousness", e.target.value)}
                        onClick={() => handleAIDefinedClick("levelOfConsciousness")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Airway Status</FormLabel>
                      <Textarea
                        value={formData.airwayStatus}
                        onChange={(e) => handleInputChange("airwayStatus", e.target.value)}
                        onClick={() => handleAIDefinedClick("airwayStatus")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Breathing Status</FormLabel>
                      <Textarea
                        value={formData.breathingStatus}
                        onChange={(e) => handleInputChange("breathingStatus", e.target.value)}
                        onClick={() => handleAIDefinedClick("breathingStatus")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Circulatory Status</FormLabel>
                      <Textarea
                        value={formData.circulatoryStatus}
                        onChange={(e) => handleInputChange("circulatoryStatus", e.target.value)}
                        onClick={() => handleAIDefinedClick("circulatoryStatus")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Background Section */}
            <GridItem>
              <Card
                bg={cardBg}
                borderRadius="xl"
                shadow="2xl"
                overflow="hidden"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "3xl" }}
              >
                <CardBody p={10}>
                  <VStack spacing={8} align="stretch">
                    <Heading size="lg" color="blue.500" fontWeight="bold">
                      Background
                    </Heading>
                    <Divider borderColor="blue.500" />

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Barriers to Care</FormLabel>
                      <Input
                        value={formData.barriersToCare}
                        onChange={(e) => handleInputChange("barriersToCare", e.target.value)}
                        onClick={() => handleAIDefinedClick("barriersToCare")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Scene Description</FormLabel>
                      <Textarea
                        value={formData.sceneDescription}
                        onChange={(e) => handleInputChange("sceneDescription", e.target.value)}
                        onClick={() => handleAIDefinedClick("sceneDescription")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Safety Consideration</FormLabel>
                      <Input
                        value={formData.safetyConsideration}
                        onChange={(e) => handleInputChange("safetyConsideration", e.target.value)}
                        onClick={() => handleAIDefinedClick("safetyConsideration")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Chief Complaint</FormLabel>
                      <Input
                        value={formData.chiefComplaint}
                        onChange={(e) => handleInputChange("chiefComplaint", e.target.value)}
                        onClick={() => handleAIDefinedClick("chiefComplaint")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Secondary Complaint</FormLabel>
                      <Input
                        value={formData.secondaryComplaint}
                        onChange={(e) => handleInputChange("secondaryComplaint", e.target.value)}
                        onClick={() => handleAIDefinedClick("secondaryComplaint")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">History of Present Illness (HPI)</FormLabel>
                      <Textarea
                        value={formData.hpi}
                        onChange={(e) => handleInputChange("hpi", e.target.value)}
                        onClick={() => handleAIDefinedClick("hpi")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Past Medical History (PMHx)</FormLabel>
                      <Input
                        value={formData.pmhx}
                        onChange={(e) => handleInputChange("pmhx", e.target.value)}
                        onClick={() => handleAIDefinedClick("pmhx")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Allergies</FormLabel>
                      <Input
                        value={formData.allergies}
                        onChange={(e) => handleInputChange("allergies", e.target.value)}
                        onClick={() => handleAIDefinedClick("allergies")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Current Medications</FormLabel>
                      <Textarea
                        value={formData.currentMedications}
                        onChange={(e) => handleInputChange("currentMedications", e.target.value)}
                        onClick={() => handleAIDefinedClick("currentMedications")}
                        placeholder="AI-Defined (click to configure)"
                        rows={3}
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Secondary Assessment Section */}
            <GridItem>
              <Card
                bg={cardBg}
                borderRadius="xl"
                shadow="2xl"
                overflow="hidden"
                transition="all 0.3s"
                _hover={{ transform: "translateY(-4px)", shadow: "3xl" }}
              >
                <CardBody p={10}>
                  <VStack spacing={8} align="stretch">
                    <Heading size="lg" color="blue.500" fontWeight="bold">
                      Secondary Assessment
                    </Heading>
                    <Divider borderColor="blue.500" />

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">HEENT</FormLabel>
                      <Input
                        value={formData.heent}
                        onChange={(e) => handleInputChange("heent", e.target.value)}
                        onClick={() => handleAIDefinedClick("heent")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Respiratory</FormLabel>
                      <Input
                        value={formData.respiratory}
                        onChange={(e) => handleInputChange("respiratory", e.target.value)}
                        onClick={() => handleAIDefinedClick("respiratory")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Cardiovascular</FormLabel>
                      <Input
                        value={formData.cardiovascular}
                        onChange={(e) => handleInputChange("cardiovascular", e.target.value)}
                        onClick={() => handleAIDefinedClick("cardiovascular")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Gastrointestinal</FormLabel>
                      <Input
                        value={formData.gi}
                        onChange={(e) => handleInputChange("gi", e.target.value)}
                        onClick={() => handleAIDefinedClick("gi")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Nervous System</FormLabel>
                      <Input
                        value={formData.nervous}
                        onChange={(e) => handleInputChange("nervous", e.target.value)}
                        onClick={() => handleAIDefinedClick("nervous")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Musculoskeletal</FormLabel>
                      <Input
                        value={formData.musculoskeletal}
                        onChange={(e) => handleInputChange("musculoskeletal", e.target.value)}
                        onClick={() => handleAIDefinedClick("musculoskeletal")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Integumentary</FormLabel>
                      <Input
                        value={formData.integumentary}
                        onChange={(e) => handleInputChange("integumentary", e.target.value)}
                        onClick={() => handleAIDefinedClick("integumentary")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Endocrine</FormLabel>
                      <Input
                        value={formData.endocrine}
                        onChange={(e) => handleInputChange("endocrine", e.target.value)}
                        onClick={() => handleAIDefinedClick("endocrine")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Genitourinary</FormLabel>
                      <Input
                        value={formData.gu}
                        onChange={(e) => handleInputChange("gu", e.target.value)}
                        onClick={() => handleAIDefinedClick("gu")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontWeight="semibold" color="gray.300">Reproductive</FormLabel>
                      <Input
                        value={formData.reproductive}
                        onChange={(e) => handleInputChange("reproductive", e.target.value)}
                        onClick={() => handleAIDefinedClick("reproductive")}
                        placeholder="AI-Defined (click to configure)"
                        bg={inputBg}
                        borderRadius="lg"
                        focusBorderColor={focusBorderColor}
                        size="lg"
                        _placeholder={{ color: "gray.500" }}
                      />
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>
          )}

          {isAdvancedMode && (
            <VStack className="no-print" spacing={4} align="stretch">
              <HStack spacing={4} justify="center">
                <Button
                  colorScheme="green"
                  bg="#00aa00"
                  size="lg"
                  width={{ base: "full", md: "auto" }}
                  px={12}
                  onClick={handleGenerate}
                  isLoading={fetching}
                >
                  Generate Scenario
                </Button>
                <Button
                  colorScheme="red"
                  variant="outline"
                  size="md"
                  onClick={clearForm}
                >
                  Clear Form
                </Button>
              </HStack>
            </VStack>
          )}

          {scenario && (
            <Box ref={scenarioRef} className="scenario-container">
              <Card bg={bgColor} borderColor="#00aa00" borderWidth="2px" shadow="lg">
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <HStack className="no-print" justify="space-between" align="center">
                      <Heading size="lg" color="#00aa00">
                        Generated Scenario
                      </Heading>
                      <HStack>
                        <Button colorScheme="teal" onClick={handlePrint}>
                          Print Scenario
                        </Button>
                        <Button colorScheme="blue" onClick={handleDownloadPDF}>
                          Download PDF
                        </Button>
                        <Button 
                          colorScheme="purple" 
                          onClick={handleCreateImmersive}
                          isLoading={savingScenario}
                          leftIcon={<ViewIcon />}
                        >
                          Create Immersive Scenario
                        </Button>
                        <Button 
                          colorScheme="orange" 
                          onClick={handleShareScenario}
                          leftIcon={<Icon as={FiShare2} />}
                          isLoading={savingScenario}
                        >
                          Share Scenario
                        </Button>
                      </HStack>
                    </HStack>
                    <Divider />
                    <ScenarioDisplay scenario={scenario} />
                  </VStack>
                </CardBody>
              </Card>
            </Box>
          )}
        </VStack>
      </Container>

      <ShareScenarioModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        scenario={savedScenarioData || scenario}
        cohort={selectedCohort}
        user={user}
      />
    </Box>
  );
}